import React, { useMemo } from "react";
import MapGL, { Layer, Marker, NavigationControl, Source } from "react-map-gl/maplibre";
import bbox from "@turf/bbox";
import { Box, Typography } from "@mui/material";

/*
 * Interactive route map for the tracking modal.
 *
 * Replaces the Mapbox Static Images URL this panel used to render. That URL
 * pointed at a style owned by a revoked account, so the <img> had a null src
 * and displayed a broken-image icon. There is no keyless static-image service
 * to substitute, but MapLibre and OpenFreeMap tiles are already in the bundle
 * and OSRM already returns the route geometry — so a real map is both cheaper
 * to build and better than the picture it replaces.
 *
 * Keyless throughout: OpenFreeMap tiles, OSRM geometry. No account, no token.
 */

const MAP_STYLE = "https://tiles.openfreemap.org/styles/positron";

// Route line uses the same blue as the dashboard's profit series.
const ROUTE_COLOR = "#2a78d6";

const PIN = {
	pickup: { color: "#eb6834", label: "P", title: "Pickup" },
	delivery: { color: "#4a3aa7", label: "D", title: "Delivery" },
	current: { color: "#1baf7a", label: "•", title: "Current location" },
	extraStop: { color: "#eda100", label: "S", title: "Additional stop" },
};

const Pin = ({ kind }) => {
	const { color, label, title } = PIN[kind];
	return (
		<div
			title={title}
			aria-label={title}
			style={{
				width: 24,
				height: 24,
				borderRadius: "50%",
				background: color,
				// 2px surface ring so overlapping pins stay separable.
				border: "2px solid #ffffff",
				boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
				color: "#ffffff",
				fontSize: 12,
				fontWeight: 700,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				cursor: "default",
			}}
		>
			{label}
		</div>
	);
};

const RouteMap = ({ geometry, points, height = 320 }) => {
	// Frame the whole route. Computed from the geometry rather than guessed, so
	// a short city run and a cross-country haul both fill the panel.
	const initialViewState = useMemo(() => {
		if (!geometry?.coordinates?.length) return null;
		try {
			const [minLon, minLat, maxLon, maxLat] = bbox(geometry);
			return {
				longitude: (minLon + maxLon) / 2,
				latitude: (minLat + maxLat) / 2,
				zoom: 5,
				fitBoundsOptions: { padding: 40 },
				bounds: [
					[minLon, minLat],
					[maxLon, maxLat],
				],
			};
		} catch {
			return null;
		}
	}, [geometry]);

	if (!geometry?.coordinates?.length || !initialViewState) {
		return (
			<Box
				sx={{
					height,
					borderRadius: "7px",
					border: "1px solid rgba(11,11,11,0.12)",
					background: "#f5f7fa",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					gap: 0.5,
					px: 3,
					textAlign: "center",
				}}
			>
				<Typography sx={{ fontSize: 14, fontWeight: 600, color: "#52514e" }}>
					Route map unavailable
				</Typography>
				<Typography sx={{ fontSize: 12, color: "#898781" }}>
					The pickup and delivery need coordinates before a route can be drawn.
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{ height, borderRadius: "7px", overflow: "hidden", border: "1px solid cornflowerblue" }}
		>
			<MapGL
				initialViewState={initialViewState}
				mapStyle={MAP_STYLE}
				style={{ width: "100%", height: "100%" }}
				attributionControl={true}
				// The modal is a read-only summary; scroll should page the modal
				// rather than zoom the map out from under the cursor.
				scrollZoom={false}
			>
				<NavigationControl position="top-right" showCompass={false} />

				<Source id="route" type="geojson" data={{ type: "Feature", geometry }}>
					{/* Casing beneath the line keeps it legible over dark map features. */}
					<Layer
						id="route-casing"
						type="line"
						layout={{ "line-cap": "round", "line-join": "round" }}
						paint={{ "line-color": "#ffffff", "line-width": 6, "line-opacity": 0.9 }}
					/>
					<Layer
						id="route-line"
						type="line"
						layout={{ "line-cap": "round", "line-join": "round" }}
						paint={{ "line-color": ROUTE_COLOR, "line-width": 3 }}
					/>
				</Source>

				{points?.pickup && (
					<Marker longitude={points.pickup[0]} latitude={points.pickup[1]} anchor="center">
						<Pin kind="pickup" />
					</Marker>
				)}
				{points?.delivery && (
					<Marker longitude={points.delivery[0]} latitude={points.delivery[1]} anchor="center">
						<Pin kind="delivery" />
					</Marker>
				)}
				{points?.extraStop && (
					<Marker longitude={points.extraStop[0]} latitude={points.extraStop[1]} anchor="center">
						<Pin kind="extraStop" />
					</Marker>
				)}
				{points?.current && (
					<Marker longitude={points.current[0]} latitude={points.current[1]} anchor="center">
						<Pin kind="current" />
					</Marker>
				)}
			</MapGL>
		</Box>
	);
};

export default RouteMap;
